import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../Toast';
import { Mail, ChevronDown, ChevronUp, Loader2, ExternalLink, Reply, Send, X, Clock, Paperclip } from 'lucide-react';
import { gmailApi } from '../../services/crmApi';

interface GmailThread {
  threadId: string;
  subject: string;
  from: string;
  to: string;
  date: string;
  snippet: string;
  messageCount: number;
  labels: string[];
}

interface GmailMessage {
  messageId: string;
  threadId: string;
  from: string;
  to: string;
  subject: string;
  date: string;
  body: string;
  attachments: { filename: string; mimeType: string; size: number }[];
}

interface Props {
  clientEmail: string;
  clientName?: string;
  account?: string;
  defaultExpanded?: boolean;
}

function parseEmailName(raw: string): string {
  const match = raw.match(/^"?(.+?)"?\s*<(.+?)>$/);
  return match ? match[1].replace(/"/g, '') : raw;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 86400000 && d.getDate() === now.getDate())
      return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    if (diff < 7 * 86400000)
      return d.toLocaleDateString('pl-PL', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
  } catch { return dateStr; }
}

const ACCOUNTS = [
  { email: 'szkolenia@fundacjanh.org', label: 'Szkolenia' },
  { email: 'turnusy@fundacjanh.org', label: 'Turnusy' },
  { email: 'dotacje@fundacjanh.org', label: 'Dotacje' },
  { email: 'biuro@fundacjanh.org', label: 'Biuro' },
];

export const GmailCorrespondence: React.FC<Props> = ({ clientEmail, clientName, account: defaultAccount, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const [threads, setThreads] = useState<GmailThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeAccount, setActiveAccount] = useState(defaultAccount || 'szkolenia@fundacjanh.org');

  // Thread detail
  const [openThread, setOpenThread] = useState<string | null>(null);
  const [threadMsgs, setThreadMsgs] = useState<GmailMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);

  // Reply
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!clientEmail) return;
    setLoading(true);
    setError('');
    try {
      const data = await gmailApi.correspondence(clientEmail, activeAccount);
      setThreads(data.threads || []);
    } catch (err: any) {
      setError(err.response?.data?.details || 'Blad pobierania korespondencji');
    } finally {
      setLoading(false);
    }
  }, [clientEmail, activeAccount]);

  useEffect(() => {
    if (expanded) load();
  }, [expanded, load]);

  const loadThread = async (threadId: string) => {
    setOpenThread(threadId);
    setThreadLoading(true);
    try {
      const data = await gmailApi.thread(threadId, activeAccount);
      setThreadMsgs(data.messages || []);
    } catch { /* */ } finally {
      setThreadLoading(false);
    }
  };

  const handleReply = async () => {
    if (!openThread || !replyText.trim() || sending) return;
    setSending(true);
    try {
      await gmailApi.reply({ account: activeAccount, threadId: openThread, body: replyText.trim() });
      setReplyText('');
      setReplying(false);
      loadThread(openThread);
    } catch (err: any) {
      toast.error(err.response?.data?.details || 'Błąd wysyłania');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-purple-600" />
          <span className="font-bold text-sm">
            Korespondencja Gmail
            {clientName && <span className="font-normal text-gray-500 ml-1">({clientName})</span>}
          </span>
          {!loading && threads.length > 0 && (
            <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 rounded-full">{threads.length}</span>
          )}
        </div>
        {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {/* Account selector */}
          <div className="px-3 py-2 flex gap-1 bg-gray-50 border-b border-gray-100">
            {ACCOUNTS.map(acc => (
              <button
                key={acc.email}
                onClick={() => { setActiveAccount(acc.email); setOpenThread(null); }}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                  activeAccount === acc.email ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-200'
                }`}
              >
                {acc.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="px-3 py-2 bg-red-50 text-red-600 text-xs">{error}</div>
          )}

          {/* Thread list or detail */}
          {openThread ? (
            <div>
              <button
                onClick={() => { setOpenThread(null); setReplying(false); }}
                className="w-full px-3 py-2 text-xs text-purple-600 hover:bg-purple-50 flex items-center gap-1 border-b"
              >
                <ChevronDown size={12} className="rotate-90" /> Wróćdo listy
              </button>

              <div className="max-h-80 overflow-y-auto p-3 space-y-3">
                {threadLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 size={16} className="animate-spin text-gray-400" />
                  </div>
                ) : (
                  threadMsgs.map(msg => {
                    const isFromUs = msg.from.includes(activeAccount);
                    const bodyIsHtml = msg.body.includes('<') && msg.body.includes('>');
                    return (
                      <div key={msg.messageId} className={`rounded-lg border p-3 ${isFromUs ? 'border-purple-200 bg-purple-50/30' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-semibold text-gray-700">{parseEmailName(msg.from)}</span>
                          <span className="text-[10px] text-gray-400 ml-auto flex items-center gap-1">
                            <Clock size={9} />{formatDate(msg.date)}
                          </span>
                        </div>
                        {bodyIsHtml ? (
                          <div className="text-xs text-gray-700 [&_*]:!text-xs overflow-hidden" dangerouslySetInnerHTML={{ __html: msg.body }} />
                        ) : (
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">{msg.body}</pre>
                        )}
                        {msg.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {msg.attachments.map((a, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded text-[9px] text-gray-500">
                                <Paperclip size={8} />{a.filename}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Reply bar */}
              {replying ? (
                <div className="p-3 border-t space-y-2">
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    rows={4}
                    placeholder="Napisz odpowiedz..."
                    className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-purple-300 resize-none"
                    onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleReply(); }}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Ctrl+Enter = wyślij | z {activeAccount}</span>
                    <div className="flex gap-2">
                      <button onClick={() => setReplying(false)} className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded">Anuluj</button>
                      <button
                        onClick={handleReply}
                        disabled={!replyText.trim() || sending}
                        className="flex items-center gap-1 px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:bg-gray-300"
                      >
                        {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Wyślij
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 border-t">
                  <button
                    onClick={() => setReplying(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 w-full justify-center"
                  >
                    <Reply size={14} /> Odpowiedz
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 size={16} className="animate-spin text-gray-400" />
                </div>
              ) : threads.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-xs">
                  <Mail size={24} className="mx-auto mb-2 opacity-40" />
                  Brak korespondencji z {clientEmail}
                </div>
              ) : (
                threads.map(thread => {
                  const isUnread = thread.labels.includes('UNREAD');
                  return (
                    <button
                      key={thread.threadId}
                      onClick={() => loadThread(thread.threadId)}
                      className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 ${isUnread ? 'bg-purple-50/30' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <p className={`text-xs truncate flex-1 ${isUnread ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                          {thread.subject}
                        </p>
                        {thread.messageCount > 1 && (
                          <span className="text-[9px] text-gray-400 bg-gray-100 px-1 rounded-full">{thread.messageCount}</span>
                        )}
                        {isUnread && <div className="w-1.5 h-1.5 bg-purple-500 rounded-full" />}
                      </div>
                      <p className="text-[10px] text-gray-400 truncate mt-0.5">{thread.snippet}</p>
                      <p className="text-[10px] text-gray-300 mt-0.5">{formatDate(thread.date)}</p>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
